import React, { useMemo, useCallback, useEffect, useState } from 'react';
import {
  TextField,
  ColorPickerField,
  SelectField,
  Card,
  Button,
  CodeEditorField,
  ToggleSwitchField,
} from '@dopomogai/ui';
import { Palette, Type, Layout, Paintbrush, RotateCcw, Settings } from 'lucide-react';
import type { WidgetConfig } from '../types/widget';
import { colorPalette, fontFamilyOptions, defaultWidgetConfig } from '../utils/widgetDefaults';
import { getGenericTopLevelSchemaFields } from '../utils/schemaUtils';

interface DesignTabContentProps {
  config: WidgetConfig;
  onConfigChange: (field: string, value: any) => void;
  isFieldVisible?: (path: string) => boolean;
  schema?: Record<string, any> | null;
  uiSchema?: Record<string, any> | null;
  extraConfig?: Record<string, any>;
  onExtraConfigChange?: (value: Record<string, any>) => void;
}

export const DesignTabContent: React.FC<DesignTabContentProps> = ({
  config,
  onConfigChange,
  isFieldVisible,
  schema,
  uiSchema,
  extraConfig,
  onExtraConfigChange,
}) => {
  const canShow = isFieldVisible ?? (() => true);

  const showIdentity = ['widgetTitle', 'collapsedMessage', 'inputPlaceholder', 'botAvatarImageUrl'].some(canShow);
  const showColors = [
    'colors.primary',
    'colors.widgetBackground',
    'colors.messagesBackground',
    'colors.userBubbleBackground',
    'colors.textColorUserBubble',
    'colors.botBubbleBackground',
    'colors.textColorBotBubble',
    'colors.botBubbleBorder',
    'colors.systemBubbleBackground',
    'colors.systemBubbleColor',
    'colors.systemBubbleBorder',
    'colors.textColorPrimary',
    'colors.textColorSecondary',
    'colors.borderColor',
    'colors.focusRingColor',
    'colors.primaryGradient',
  ].some(canShow);
  const showTypography = ['appearance.fontFamily', 'appearance.fontSizeBase'].some(canShow);
  const showLayout = ['appearance.widgetMaxWidth', 'appearance.widgetMaxHeight'].some(canShow);
  const showShadows = ['appearance.widgetShadow', 'appearance.bubbleShadow'].some(canShow);

  const [extraJson, setExtraJson] = useState(() => JSON.stringify(extraConfig ?? {}, null, 2));
  const [extraError, setExtraError] = useState<string | null>(null);

  useEffect(() => {
    setExtraJson(JSON.stringify(extraConfig ?? {}, null, 2));
    setExtraError(null);
  }, [extraConfig]);

  const handleExtraChange = useCallback((code: string) => {
    setExtraJson(code);
    if (!onExtraConfigChange) return;
    try {
      const parsed = code.trim() ? JSON.parse(code) : {};
      setExtraError(null);
      onExtraConfigChange(parsed);
    } catch (err: any) {
      setExtraError('Invalid JSON. Fix formatting to apply changes.');
    }
  }, [onExtraConfigChange]);

  const backgroundPalette = useMemo(
    () => ['#FFFFFF', '#F9FAFB', '#F3F4F6', '#E5E7EB', '#F1F5F9', '#E2E8F0'],
    []
  );
  const textPrimaryPalette = useMemo(
    () => ['#1A1A1A', '#111827', '#374151', '#4B5563', '#6B7280'],
    []
  );
  const textSecondaryPalette = useMemo(
    () => ['#555555', '#6B7280', '#9CA3AF', '#D1D5DB'],
    []
  );
  const botBubblePalette = useMemo(
    () => ['#ECECEC', '#F3F4F6', '#E5E7EB', '#D1D5DB', '#FFFFFF'],
    []
  );

  const handleResetColors = useCallback(() => {
    const defaults = defaultWidgetConfig.colors;
    onConfigChange('colors', { ...defaults });
  }, [onConfigChange]);

  const genericTopLevelFields = useMemo(
    () => getGenericTopLevelSchemaFields(schema, uiSchema),
    [schema, uiSchema]
  );

  const handleGenericExtraChange = useCallback((key: string, value: unknown) => {
    if (!onExtraConfigChange) return;
    const next = { ...(extraConfig || {}) };
    next[key] = value;
    onExtraConfigChange(next);
  }, [extraConfig, onExtraConfigChange]);

  return (
    <div className="space-y-6">
      {showIdentity && (
        <Card
          title="Widget Identity"
          icon={<Type className="w-5 h-5" />}
          noPadding={false}
          headerBgColor="bg-indigo-100"
        >
          <div className="space-y-4">
            {canShow('widgetTitle') && (
              <TextField
                label="Widget Title"
                value={config.widgetTitle}
                onChange={(e) => onConfigChange('widgetTitle', e.target.value)}
                placeholder="AI Assistant"
                data-testid="widget-editor-title-input"
              />
            )}

            {canShow('collapsedMessage') && (
              <TextField
                label="Collapsed Message"
                value={config.collapsedMessage}
                onChange={(e) => onConfigChange('collapsedMessage', e.target.value)}
                placeholder="Need Help? Ask {botName}!"
              />
            )}

            {canShow('inputPlaceholder') && (
              <TextField
                label="Input Placeholder"
                value={config.inputPlaceholder}
                onChange={(e) => onConfigChange('inputPlaceholder', e.target.value)}
                placeholder="Type your message..."
              />
            )}

            {canShow('botAvatarImageUrl') && (
              <TextField
                label="Avatar Image URL"
                value={config.botAvatarImageUrl ?? ''}
                onChange={(e) => onConfigChange('botAvatarImageUrl', e.target.value)}
                placeholder="https://example.com/avatar.png"
              />
            )}
          </div>
        </Card>
      )}

      {showColors && (
        <Card
          title="Colors"
          icon={<Palette className="w-5 h-5" />}
          noPadding={false}
          headerBgColor="bg-rose-100"
          actionButton={
            <Button
              size="sm"
              variant="ghost"
              icon={<RotateCcw className="w-4 h-4" />}
              onClick={handleResetColors}
              aria-label="Reset colors to defaults"
            >
              Reset Colors
            </Button>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {canShow('colors.primary') && (
              <div className="relative">
                <ColorPickerField
                  label="Primary Color"
                  value={config.colors.primary}
                  onChange={(color) => onConfigChange('colors.primary', color)}
                  paletteColors={colorPalette}
                  portal
                />
              </div>
            )}

            {canShow('colors.widgetBackground') && (
              <div className="relative">
                <ColorPickerField
                  label="Widget Background"
                  value={config.colors.widgetBackground}
                  onChange={(color) => onConfigChange('colors.widgetBackground', color)}
                  paletteColors={backgroundPalette}
                  portal
                />
              </div>
            )}

            {canShow('colors.messagesBackground') && (
              <div className="relative">
                <ColorPickerField
                  label="Messages Background"
                  value={config.colors.messagesBackground}
                  onChange={(color) => onConfigChange('colors.messagesBackground', color)}
                  paletteColors={backgroundPalette}
                  portal
                />
              </div>
            )}

            {canShow('colors.userBubbleBackground') && (
              <div className="relative">
                <ColorPickerField
                  label="User Bubble Background"
                  value={config.colors.userBubbleBackground}
                  onChange={(color) => onConfigChange('colors.userBubbleBackground', color)}
                  paletteColors={colorPalette}
                  portal
                />
              </div>
            )}
            {canShow('colors.textColorUserBubble') && (
              <div className="relative">
                <ColorPickerField
                  label="User Bubble Text"
                  value={config.colors.textColorUserBubble}
                  onChange={(color) => onConfigChange('colors.textColorUserBubble', color)}
                  paletteColors={colorPalette}
                  portal
                />
              </div>
            )}

            {canShow('colors.botBubbleBackground') && (
              <div className="relative">
                <ColorPickerField
                  label="Assistant Bubble Background"
                  value={config.colors.botBubbleBackground}
                  onChange={(color) => onConfigChange('colors.botBubbleBackground', color)}
                  paletteColors={botBubblePalette}
                  portal
                />
              </div>
            )}
            {canShow('colors.textColorBotBubble') && (
              <div className="relative">
                <ColorPickerField
                  label="Assistant Bubble Text"
                  value={config.colors.textColorBotBubble}
                  onChange={(color) => onConfigChange('colors.textColorBotBubble', color)}
                  paletteColors={textPrimaryPalette}
                  portal
                />
              </div>
            )}
            {canShow('colors.botBubbleBorder') && (
              <div className="relative">
                <ColorPickerField
                  label="Assistant Bubble Border"
                  value={config.colors.botBubbleBorder}
                  onChange={(color) => onConfigChange('colors.botBubbleBorder', color)}
                  paletteColors={textSecondaryPalette}
                  portal
                />
              </div>
            )}

            {canShow('colors.systemBubbleBackground') && (
              <div className="relative">
                <ColorPickerField
                  label="System Bubble Background"
                  value={config.colors.systemBubbleBackground}
                  onChange={(color) => onConfigChange('colors.systemBubbleBackground', color)}
                  paletteColors={botBubblePalette}
                  portal
                />
              </div>
            )}
            {canShow('colors.systemBubbleColor') && (
              <div className="relative">
                <ColorPickerField
                  label="System Bubble Text"
                  value={config.colors.systemBubbleColor}
                  onChange={(color) => onConfigChange('colors.systemBubbleColor', color)}
                  paletteColors={textPrimaryPalette}
                  portal
                />
              </div>
            )}
            {canShow('colors.systemBubbleBorder') && (
              <div className="relative">
                <ColorPickerField
                  label="System Bubble Border"
                  value={config.colors.systemBubbleBorder}
                  onChange={(color) => onConfigChange('colors.systemBubbleBorder', color)}
                  paletteColors={textSecondaryPalette}
                  portal
                />
              </div>
            )}

            {canShow('colors.textColorPrimary') && (
              <div className="relative">
                <ColorPickerField
                  label="Primary Text"
                  value={config.colors.textColorPrimary}
                  onChange={(color) => onConfigChange('colors.textColorPrimary', color)}
                  paletteColors={textPrimaryPalette}
                  portal
                />
              </div>
            )}

            {canShow('colors.textColorSecondary') && (
              <div className="relative">
                <ColorPickerField
                  label="Secondary Text"
                  value={config.colors.textColorSecondary}
                  onChange={(color) => onConfigChange('colors.textColorSecondary', color)}
                  paletteColors={textSecondaryPalette}
                  portal
                />
              </div>
            )}

            {canShow('colors.borderColor') && (
              <div className="relative">
                <ColorPickerField
                  label="Border Color"
                  value={config.colors.borderColor}
                  onChange={(color) => onConfigChange('colors.borderColor', color)}
                  paletteColors={textSecondaryPalette}
                  portal
                />
              </div>
            )}

            {canShow('colors.focusRingColor') && (
              <div className="relative">
                <ColorPickerField
                  label="Focus Ring Color"
                  value={config.colors.focusRingColor}
                  onChange={(color) => onConfigChange('colors.focusRingColor', color)}
                  paletteColors={textSecondaryPalette}
                  portal
                />
              </div>
            )}

            {canShow('colors.primaryGradient') && (
              <div className="relative">
                <TextField
                  label="Primary Gradient"
                  value={config.colors.primaryGradient}
                  onChange={(e) => onConfigChange('colors.primaryGradient', e.target.value)}
                  placeholder="linear-gradient(135deg, #6366f1, #4f46e5)"
                />
              </div>
            )}
          </div>
        </Card>
      )}

      {showTypography && (
        <Card
          title="Typography"
          icon={<Type className="w-5 h-5" />}
          noPadding={false}
          headerBgColor="bg-violet-100"
        >
          <div className="space-y-4">
            {canShow('appearance.fontFamily') && (
              <SelectField
                label="Font Family"
                name="fontFamily"
                value={config.appearance.fontFamily}
                options={fontFamilyOptions}
                onChange={(value) => onConfigChange('appearance.fontFamily', value)}
              />
            )}

            {canShow('appearance.fontSizeBase') && (
              <TextField
                label="Base Font Size"
                value={config.appearance.fontSizeBase}
                onChange={(e) => onConfigChange('appearance.fontSizeBase', e.target.value)}
                placeholder="16px"
              />
            )}
          </div>
        </Card>
      )}

      {showLayout && (
        <Card
          title="Layout & Dimensions"
          icon={<Layout className="w-5 h-5" />}
          noPadding={false}
          headerBgColor="bg-amber-100"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {canShow('appearance.widgetMaxWidth') && (
              <TextField
                label="Widget Max Width"
                value={config.appearance.widgetMaxWidth}
                onChange={(e) => onConfigChange('appearance.widgetMaxWidth', e.target.value)}
                placeholder="380px"
              />
            )}

            {canShow('appearance.widgetMaxHeight') && (
              <TextField
                label="Widget Max Height"
                value={config.appearance.widgetMaxHeight}
                onChange={(e) => onConfigChange('appearance.widgetMaxHeight', e.target.value)}
                placeholder="640px"
              />
            )}
          </div>
        </Card>
      )}

      {showShadows && (
        <Card
          title="Shadows & Effects"
          icon={<Paintbrush className="w-5 h-5" />}
          noPadding={false}
          headerBgColor="bg-teal-100"
        >
          <div className="space-y-4">
            {canShow('appearance.widgetShadow') && (
              <TextField
                label="Widget Shadow"
                value={config.appearance.widgetShadow}
                onChange={(e) => onConfigChange('appearance.widgetShadow', e.target.value)}
                placeholder="0 8px 24px rgba(0, 0, 0, 0.12), ..."
              />
            )}

            {canShow('appearance.bubbleShadow') && (
              <TextField
                label="Bubble Shadow"
                value={config.appearance.bubbleShadow}
                onChange={(e) => onConfigChange('appearance.bubbleShadow', e.target.value)}
                placeholder="0 1px 3px rgba(0,0,0,0.06)"
              />
            )}
          </div>
        </Card>
      )}

      {schema && onExtraConfigChange && genericTopLevelFields.length > 0 && (
        <Card
          title="Additional Schema Fields"
          icon={<Settings className="w-5 h-5" />}
          noPadding={false}
          headerBgColor="bg-cyan-100"
        >
          <div className="space-y-4">
            {genericTopLevelFields.map((field) => {
              const rawValue = extraConfig?.[field.key];
              if (field.type === 'boolean') {
                return (
                  <div key={field.key}>
                    <ToggleSwitchField
                      label={field.label}
                      checked={Boolean(rawValue)}
                      onChange={(e) => handleGenericExtraChange(field.key, e.target.checked)}
                    />
                    {field.description && <p className="mt-1 text-xs text-slate-500">{field.description}</p>}
                  </div>
                );
              }

              if (field.type === 'enum' && field.enumValues) {
                const options = field.enumValues.map((value) => ({
                  value: String(value),
                  label: String(value),
                }));
                return (
                  <div key={field.key}>
                    <SelectField
                      label={field.label}
                      name={field.key}
                      value={rawValue === undefined || rawValue === null ? '' : String(rawValue)}
                      options={options}
                      onChange={(value) => handleGenericExtraChange(field.key, value)}
                      placeholder={`Select ${field.label}`}
                    />
                    {field.description && <p className="mt-1 text-xs text-slate-500">{field.description}</p>}
                  </div>
                );
              }

              const stringValue =
                rawValue === undefined || rawValue === null ? '' : String(rawValue);
              const numeric = field.type === 'number' || field.type === 'integer';
              return (
                <div key={field.key}>
                  <TextField
                    label={field.label}
                    value={stringValue}
                    onChange={(e) => {
                      if (!numeric) {
                        handleGenericExtraChange(field.key, e.target.value);
                        return;
                      }
                      const nextValue = e.target.value.trim();
                      if (nextValue === '') {
                        handleGenericExtraChange(field.key, null);
                        return;
                      }
                      const parsed = Number(nextValue);
                      if (Number.isNaN(parsed)) return;
                      handleGenericExtraChange(field.key, field.type === 'integer' ? Math.trunc(parsed) : parsed);
                    }}
                    placeholder={field.key}
                  />
                  {field.description && <p className="mt-1 text-xs text-slate-500">{field.description}</p>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {schema && onExtraConfigChange && (
        <Card
          title="Additional Config (Schema Extras)"
          icon={<Settings className="w-5 h-5" />}
          noPadding={false}
          headerBgColor="bg-slate-100"
        >
          <div className="space-y-3">
            <CodeEditorField
              label="Extra Config JSON"
              value={extraJson}
              onChange={handleExtraChange}
              language="json"
              height="220px"
              errorText={extraError || undefined}
            />
            <p className="text-xs text-slate-500">
              This block captures schema fields not covered by the standard UI. It is merged on save and preview.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};
